import type { DevEvent } from './types';

export type EventFilter = 'all' | 'failures' | 'replays' | 'signatures' | 'domain' | 'keys' | 'consent' | 'verify' | 'mandates' | 'service';

export const EVENT_FILTERS: { id: EventFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'failures', label: 'Failures' },
  { id: 'replays', label: 'Replays' },
  { id: 'signatures', label: 'Signatures' },
  { id: 'domain', label: 'Domain' },
  { id: 'keys', label: 'Keys' },
  { id: 'consent', label: 'Login & consent' },
  { id: 'verify', label: 'Verify' },
  { id: 'mandates', label: 'Mandates' },
  { id: 'service', label: 'Service' },
];

export const isFailure = (e: DevEvent) => e.result_code !== 'ok';

export function matchesFilter(e: DevEvent, f: EventFilter): boolean {
  const k = e.kind;
  switch (f) {
    case 'all':
      return true;
    case 'failures':
      return isFailure(e);
    case 'replays':
      return k === 'signature.replay' || e.result_code === 'replay_detected';
    case 'signatures':
      return k.startsWith('signature.');
    case 'domain':
      return k.startsWith('domain.');
    case 'keys':
      return k.startsWith('keys.') || k.startsWith('key.');
    case 'consent':
      return k.startsWith('consent.') || k.startsWith('login.') || k.startsWith('token.');
    case 'verify':
      return k.startsWith('verify.');
    case 'mandates':
      return k.startsWith('authorization.') || k.startsWith('mandate.');
    case 'service':
      return k.startsWith('service.') || k.startsWith('activation.') || k.startsWith('redirects.');
  }
}

const DESCRIPTIONS: Record<string, string> = {
  'signature.failed': 'A signed request to Pryme failed verification',
  'signature.replay': 'A signed request reused a nonce (replay blocked)',
  'domain.challenge_issued': 'DNS TXT challenge issued',
  'domain.verification': 'Domain verification checked',
  'keys.provisioned': 'Credentials provisioned',
  'keys.rotated': 'Credentials rotated',
  'key.revoked': 'Key revoked',
  'login.started': 'A user started “Continue with Pryme”',
  'consent.granted': 'A user approved the consent screen',
  'consent.rejected': 'A user rejected the consent screen',
  'token.issued': 'Authorization code exchanged',
  'token.rejected': 'Code exchange rejected',
  'verify.state_issued': 'Signed claim state issued',
  'verify.rejected': 'Verify request rejected',
  'authorization.allow': 'Mandate check: allowed',
  'authorization.approval_required': 'Mandate check: human approval required',
  'authorization.deny': 'Mandate check: denied',
  'authorization.denied': 'Mandate check: denied (routing)',
  'service.created': 'Service created',
  'service.activated': 'Service activated by signed challenge',
  'activation.failed': 'Activation attempt failed',
  'redirects.updated': 'Redirect URIs updated',
};

export const describeEvent = (kind: string) => DESCRIPTIONS[kind] ?? kind;
